import { CheckCircleTwoTone, CheckSquareTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { responsibilityChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: "What's the difference between responsibility and just doing what you're told?",
    answer: (
      <>
        Responsibility means you take ownership of your duties without needing someone to remind you or check up on you. When you're showing
        up on time for work or school, you're demonstrating this same kind of self-management that's covered in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link>.
      </>
    ),
  },
  {
    question: "What if I finish my assigned tasks but they're not done well?",
    answer: (
      <>
        Completing a task poorly is barely better than not doing it at all — people will notice and trust you less. Take the extra few
        minutes to check your work. This attention to detail is the same skill used in{' '}
        <Link to="/adulting-101/problem-solving">Problem Solving</Link> when you make sure a solution actually works before moving on.
      </>
    ),
  },
  {
    question: "What if I don't want to do my chores?",
    answer: (
      <>
        Feeling like you don't want to do something is normal, but responsibility means doing it anyway. Think of it this way: if you live
        in a home, you're part of keeping it running. For more on why this matters, see{' '}
        <Link to="/adulting-101/household-care">Household Care</Link>.
      </>
    ),
  },
  {
    question: 'Why does the quality of my chores matter, not just finishing them?',
    answer: (
      <>
        Rushing through a chore just to say it's "done" usually means it needs to be redone, which wastes everyone's time. Doing things
        properly the first time is a habit that pays off, similar to budgeting carefully in{' '}
        <Link to="/adulting-101/financial-literacy">Financial Literacy</Link> instead of cutting corners.
      </>
    ),
  },
  {
    question: 'How do I stay on top of homework without falling behind?',
    answer: (
      <>
        Do a little bit each day instead of cramming everything at once. Write down assignments as soon as you get them. This is the exact
        planning skill covered in <Link to="/adulting-101/time-management">Time Management</Link> — a little consistency each day beats a
        scramble later.
      </>
    ),
  },
  {
    question: 'Why does paying attention in class or at work actually matter?',
    answer: (
      <>
        Just showing up isn't the same as engaging. Paying attention means you actually learn or absorb what's happening, so you don't have
        to redo work or ask others to repeat themselves. It's a form of respect, similar to active listening in{' '}
        <Link to="/adulting-101/communication-skills">Communication Skills</Link>.
      </>
    ),
  },
  {
    question: 'What if I make a mistake at work or school? Should I tell someone?',
    answer: (
      <>
        Yes. Admitting mistakes early shows maturity and responsibility. Your boss, teacher, or parent will respect you more for owning up
        to it than for trying to hide it. This kind of honesty also matters in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link>, where trust is built by telling the truth even when it's
        uncomfortable.
      </>
    ),
  },
  {
    question: "How do I know if I'm being responsible?",
    answer: (
      <>
        If your boss, teacher, or parent trusts you to do things without constant reminders, you're on the right track. Following through on
        your word is closely tied to <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link> — people notice when your
        actions match what you say you'll do.
      </>
    ),
  },
]

export function Adulting101ResponsibilityPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Responsibility</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #fff7e6 0%, #ffffff 220px)' }}>
        <Title level={2}>Responsibility means doing what you\'re supposed to do, even when no one is watching.</Title>
        <Paragraph type="secondary">
          Adults who get jobs, keep friendships, and earn trust are the ones who follow through on their commitments. Responsibility is the
          foundation of becoming someone people can count on.
        </Paragraph>

        <Card
          title={
            <Space>
              <CheckSquareTwoTone />
              Responsibility Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you\'re being a responsible adult!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Responsibility is built one day at a time. The more consistently you follow through, the more people will trust and respect
              you.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Responsibility
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
